/**
 * LLM-Powered Workflow Editor Hook
 *
 * Provides state management and API integration for LLM-assisted
 * workflow editing with draft support.
 *
 * Features:
 * - Generate suggestions from natural language prompts
 * - In-memory draft state with auto-save
 * - Undo/redo support
 * - Definition export
 * - Valkey/Wasabi persistence
 *
 * DRR-056: Data-Driven Workflow Configuration
 */

import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Operations
// ============================================================================

const GENERATE_SUGGESTIONS = gql`
  mutation GenerateSuggestions($input: GenerateSuggestionsInput!, $userId: String!) {
    generateSuggestions(input: $input, userId: $userId) {
      suggestions {
        suggestionId
        operation
        configPatch
        explanation
        confidence
        affectedNodes
        affectedEdges
        warnings
        complianceImpact
        suggestedLevel
      }
      currentWorkflow {
        id
        name
        version
        nodes {
          id
          nodeType
          label
          description
          criticality
          insertAfter
          insertBefore
          remove
        }
        edges {
          id
          source
          target
          condition
          remove
        }
      }
      draftId
    }
  }
`;

const CREATE_DRAFT = gql`
  mutation CreateDraft($input: CreateDraftInput!, $userId: String!) {
    createDraft(input: $input, userId: $userId) {
      draft {
        draftId
        workflowName
        currentState {
          id
          name
          version
          nodes {
            id
            nodeType
            label
            description
            criticality
          }
          edges {
            id
            source
            target
            condition
          }
        }
        editHistory {
          suggestionId
          appliedAt
          appliedBy
        }
        pendingSuggestions {
          suggestionId
          operation
          configPatch
          explanation
          confidence
        }
        metadata {
          createdAt
          updatedAt
          createdBy
          status
          autoSave
        }
      }
      currentDefinition
      message
    }
  }
`;

const APPLY_SUGGESTION = gql`
  mutation ApplySuggestion($input: ApplySuggestionInput!, $userId: String!) {
    applySuggestion(input: $input, userId: $userId) {
      draft {
        draftId
        workflowName
        currentState {
          id
          name
          version
          nodes {
            id
            nodeType
            label
            description
            criticality
            insertAfter
            insertBefore
            remove
          }
          edges {
            id
            source
            target
            condition
            remove
          }
        }
        editHistory {
          suggestionId
          appliedAt
          appliedBy
          definitionBefore
          definitionAfter
        }
        pendingSuggestions {
          suggestionId
          operation
          configPatch
          explanation
          confidence
        }
        metadata {
          createdAt
          updatedAt
          createdBy
          status
        }
      }
      currentDefinition
      message
    }
  }
`;

const UNDO_LAST_EDIT = gql`
  mutation UndoLastEdit($draftId: String!) {
    undoLastEdit(draftId: $draftId) {
      draft {
        draftId
        currentState {
          id
          name
          version
          nodes {
            id
            nodeType
            label
          }
          edges {
            id
            source
            target
          }
        }
        editHistory {
          suggestionId
          appliedAt
        }
      }
      currentDefinition
      message
    }
  }
`;

const DISCARD_DRAFT = gql`
  mutation DiscardDraft($draftId: String!) {
    discardDraft(draftId: $draftId)
  }
`;

const GET_DRAFT = gql`
  query GetDraft($draftId: String!) {
    getDraft(draftId: $draftId) {
      draftId
      workflowName
      currentState {
        id
        name
        version
        nodes {
          id
          nodeType
          label
          description
          criticality
        }
        edges {
          id
          source
          target
          condition
        }
      }
      editHistory {
        suggestionId
        appliedAt
        appliedBy
      }
      pendingSuggestions {
        suggestionId
        operation
        configPatch
        explanation
        confidence
      }
      metadata {
        createdAt
        updatedAt
        createdBy
        status
        autoSave
      }
    }
  }
`;

const LIST_DRAFTS = gql`
  query ListDrafts($tenantId: String!, $userId: String!) {
    listDrafts(tenantId: $tenantId, userId: $userId) {
      draftId
      workflowName
      status
      createdAt
      updatedAt
      editCount
    }
  }
`;

const EXPORT_DRAFT = gql`
  query ExportDraft($draftId: String!) {
    exportDraft(draftId: $draftId) {
      definition
      filename
      suggestedLevel
    }
  }
`;

const PERSIST_DRAFT = gql`
  mutation PersistDraft($input: PersistDraftInput!) {
    persistDraft(input: $input) {
      draft {
        draftId
        metadata {
          status
          updatedAt
        }
      }
      message
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type WorkflowEditOperation =
  | 'AddNode'
  | 'ModifyNode'
  | 'RemoveNode'
  | 'AddEdge'
  | 'ModifyEdge'
  | 'RemoveEdge'
  | 'ModifyMetadata'
  | 'AddValidationRule'
  | 'AddComplianceCheck';

export type DraftStatus =
  | 'Active'
  | 'Paused'
  | 'PendingReview'
  | 'Approved'
  | 'Rejected'
  | 'Published'
  | 'Discarded';

export interface LlmEditSuggestion {
  suggestionId: string;
  operation: WorkflowEditOperation;
  configPatch: string;
  explanation: string;
  confidence: number;
  affectedNodes: string[];
  affectedEdges: string[];
  warnings: string[];
  complianceImpact: boolean;
  suggestedLevel: string;
}

export interface WorkflowNode {
  id: string;
  nodeType: string;
  label: string;
  description?: string;
  criticality: string;
  insertAfter?: string;
  insertBefore?: string;
  remove?: boolean;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  condition?: string;
  remove?: boolean;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  version: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface AppliedEdit {
  suggestionId: string;
  appliedAt: string;
  appliedBy: string;
  definitionBefore?: string;
  definitionAfter?: string;
}

export interface DraftMetadata {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  status: DraftStatus;
  autoSave: boolean;
}

export interface WorkflowDraft {
  draftId: string;
  workflowName: string;
  currentState: WorkflowDefinition;
  editHistory: AppliedEdit[];
  pendingSuggestions: LlmEditSuggestion[];
  metadata: DraftMetadata;
}

export interface DraftSummary {
  draftId: string;
  workflowName: string;
  status: DraftStatus;
  createdAt: string;
  updatedAt: string;
  editCount: number;
}

export interface EditContext {
  tenantId: string;
  state?: string;
  industry?: string;
  programVariant?: string;
  certification?: string;
}

// ============================================================================
// State Management
// ============================================================================

interface EditorState {
  // Current draft
  draft: WorkflowDraft | null;
  // Current definition representation
  currentDefinition: string;
  // Pending suggestions from LLM
  pendingSuggestions: LlmEditSuggestion[];
  // Selected suggestion for preview
  selectedSuggestion: LlmEditSuggestion | null;
  // Loading states
  isGenerating: boolean;
  isApplying: boolean;
  isSaving: boolean;
  // Error state
  error: string | null;
  // Undo stack (for local undo before committing)
  undoStack: WorkflowDefinition[];
  // Redo stack
  redoStack: WorkflowDefinition[];
  // Prompt history
  promptHistory: string[];
  // Edit mode
  editMode: 'prompt' | 'manual' | 'review';
}

type EditorAction =
  | { type: 'SET_DRAFT'; draft: WorkflowDraft; definition: string }
  | { type: 'SET_SUGGESTIONS'; suggestions: LlmEditSuggestion[] }
  | { type: 'SELECT_SUGGESTION'; suggestion: LlmEditSuggestion | null }
  | { type: 'APPLY_SUGGESTION_START' }
  | { type: 'APPLY_SUGGESTION_SUCCESS'; draft: WorkflowDraft; definition: string }
  | { type: 'UNDO_SUCCESS'; draft: WorkflowDraft; definition: string }
  | { type: 'SET_GENERATING'; isGenerating: boolean }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'ADD_PROMPT'; prompt: string }
  | { type: 'SET_EDIT_MODE'; mode: 'prompt' | 'manual' | 'review' }
  | { type: 'LOCAL_UPDATE'; definition: WorkflowDefinition }
  | { type: 'LOCAL_UNDO' }
  | { type: 'LOCAL_REDO' }
  | { type: 'CLEAR_DRAFT' };

const initialState: EditorState = {
  draft: null,
  currentDefinition: '',
  pendingSuggestions: [],
  selectedSuggestion: null,
  isGenerating: false,
  isApplying: false,
  isSaving: false,
  error: null,
  undoStack: [],
  redoStack: [],
  promptHistory: [],
  editMode: 'prompt',
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_DRAFT':
      return {
        ...state,
        draft: action.draft,
        currentDefinition: action.definition,
        pendingSuggestions: action.draft.pendingSuggestions,
        error: null,
      };

    case 'SET_SUGGESTIONS':
      return {
        ...state,
        pendingSuggestions: action.suggestions,
        isGenerating: false,
        editMode: 'review',
      };

    case 'SELECT_SUGGESTION':
      return {
        ...state,
        selectedSuggestion: action.suggestion,
      };

    case 'APPLY_SUGGESTION_START':
      return {
        ...state,
        isApplying: true,
      };

    case 'APPLY_SUGGESTION_SUCCESS':
      return {
        ...state,
        draft: action.draft,
        currentDefinition: action.definition,
        pendingSuggestions: action.draft.pendingSuggestions,
        selectedSuggestion: null,
        isApplying: false,
        // Clear undo stack since we committed to backend
        undoStack: [],
        redoStack: [],
      };

    case 'UNDO_SUCCESS':
      return {
        ...state,
        draft: action.draft,
        currentDefinition: action.definition,
      };

    case 'SET_GENERATING':
      return {
        ...state,
        isGenerating: action.isGenerating,
      };

    case 'SET_SAVING':
      return {
        ...state,
        isSaving: action.isSaving,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        isGenerating: false,
        isApplying: false,
        isSaving: false,
      };

    case 'ADD_PROMPT':
      return {
        ...state,
        promptHistory: [...state.promptHistory, action.prompt],
      };

    case 'SET_EDIT_MODE':
      return {
        ...state,
        editMode: action.mode,
      };

    case 'LOCAL_UPDATE':
      if (!state.draft) return state;
      return {
        ...state,
        undoStack: [...state.undoStack, state.draft.currentState],
        redoStack: [],
        draft: {
          ...state.draft,
          currentState: action.definition,
        },
      };

    case 'LOCAL_UNDO':
      if (state.undoStack.length === 0 || !state.draft) return state;
      const prevState = state.undoStack[state.undoStack.length - 1]!;
      return {
        ...state,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.draft.currentState],
        draft: {
          ...state.draft,
          currentState: prevState,
        },
      };

    case 'LOCAL_REDO':
      if (state.redoStack.length === 0 || !state.draft) return state;
      const nextState = state.redoStack[state.redoStack.length - 1]!;
      return {
        ...state,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, state.draft.currentState],
        draft: {
          ...state.draft,
          currentState: nextState,
        },
      };

    case 'CLEAR_DRAFT':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface UseLlmWorkflowEditorOptions {
  userId: string;
  context: EditContext;
  autoSave?: boolean;
  autoSaveIntervalMs?: number;
}

export interface UseLlmWorkflowEditorResult {
  // State
  state: EditorState;

  // Draft management
  createDraft: (workflowName: string) => Promise<void>;
  loadDraft: (draftId: string) => Promise<void>;
  discardDraft: () => Promise<void>;
  persistDraft: (newStatus?: DraftStatus) => Promise<void>;

  // LLM suggestions
  generateSuggestions: (prompt: string, targetNodeId?: string) => Promise<void>;
  applySuggestion: (suggestionId: string) => Promise<void>;
  selectSuggestion: (suggestion: LlmEditSuggestion | null) => void;

  // Editing
  undo: () => void;
  redo: () => void;
  undoFromServer: () => Promise<void>;
  updateLocalDefinition: (definition: WorkflowDefinition) => void;
  setEditMode: (mode: 'prompt' | 'manual' | 'review') => void;

  // Export
  exportDefinition: () => Promise<{ definition: string; filename: string } | null>;

  // Utilities
  canUndo: boolean;
  canRedo: boolean;
  canUndoFromServer: boolean;
  hasUnsavedChanges: boolean;
  draftsList: DraftSummary[];
  draftsLoading: boolean;
  refetchDrafts: () => void;
}

export function useLlmWorkflowEditor(
  options: UseLlmWorkflowEditorOptions,
): UseLlmWorkflowEditorResult {
  const { userId, context, autoSave = true, autoSaveIntervalMs = 30000 } = options;

  const [state, dispatch] = useReducer(editorReducer, initialState);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // GraphQL mutations
  const [generateSuggestionsMutation] = useMutation<Record<string, any>>(GENERATE_SUGGESTIONS);
  const [createDraftMutation] = useMutation<Record<string, any>>(CREATE_DRAFT);
  const [applySuggestionMutation] = useMutation<Record<string, any>>(APPLY_SUGGESTION);
  const [undoLastEditMutation] = useMutation<Record<string, any>>(UNDO_LAST_EDIT);
  const [discardDraftMutation] = useMutation<Record<string, any>>(DISCARD_DRAFT);
  const [persistDraftMutation] = useMutation<Record<string, any>>(PERSIST_DRAFT);

  // GraphQL queries
  const [loadDraftQuery] = useLazyQuery<Record<string, any>>(GET_DRAFT);
  const [exportDraftQuery] = useLazyQuery<Record<string, any>>(EXPORT_DRAFT);

  const {
    data: draftsData,
    loading: draftsLoading,
    refetch: refetchDrafts,
  } = useQuery<Record<string, any>>(LIST_DRAFTS, {
    variables: { tenantId: context.tenantId, userId },
    fetchPolicy: 'cache-and-network',
  });

  // ========================================================================
  // Draft Management
  // ========================================================================

  const createDraft = useCallback(
    async (workflowName: string) => {
      try {
        const result = await createDraftMutation({
          variables: {
            input: {
              workflowName,
              tenantId: context.tenantId,
              state: context.state,
              industry: context.industry,
              programVariant: context.programVariant,
              certification: context.certification,
            },
            userId,
          },
        });

        if (result.data?.createDraft) {
          dispatch({
            type: 'SET_DRAFT',
            draft: result.data?.createDraft.draft,
            definition: result.data?.createDraft.currentDefinition,
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [createDraftMutation, context, userId],
  );

  const loadDraft = useCallback(
    async (draftId: string) => {
      try {
        const result = await loadDraftQuery({ variables: { draftId } });

        if (result.data?.getDraft) {
          dispatch({
            type: 'SET_DRAFT',
            draft: result.data?.getDraft,
            definition: '', // Will be populated on next export
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [loadDraftQuery],
  );

  const discardDraft = useCallback(async () => {
    if (!state.draft) return;

    try {
      await discardDraftMutation({
        variables: { draftId: state.draft.draftId },
      });
      dispatch({ type: 'CLEAR_DRAFT' });
      refetchDrafts();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.draft, discardDraftMutation, refetchDrafts]);

  const persistDraft = useCallback(
    async (newStatus?: DraftStatus) => {
      if (!state.draft) return;

      dispatch({ type: 'SET_SAVING', isSaving: true });

      try {
        await persistDraftMutation({
          variables: {
            input: {
              draftId: state.draft.draftId,
              newStatus,
            },
          },
        });
        dispatch({ type: 'SET_SAVING', isSaving: false });
        refetchDrafts();
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [state.draft, persistDraftMutation, refetchDrafts],
  );

  // ========================================================================
  // LLM Suggestions
  // ========================================================================

  const generateSuggestions = useCallback(
    async (prompt: string, targetNodeId?: string) => {
      if (!state.draft) return;

      dispatch({ type: 'SET_GENERATING', isGenerating: true });
      dispatch({ type: 'ADD_PROMPT', prompt });

      try {
        const result = await generateSuggestionsMutation({
          variables: {
            input: {
              prompt,
              workflowName: state.draft.workflowName,
              tenantId: context.tenantId,
              state: context.state,
              industry: context.industry,
              programVariant: context.programVariant,
              certification: context.certification,
              targetNodeId,
            },
            userId,
          },
        });

        if (result.data?.generateSuggestions) {
          dispatch({
            type: 'SET_SUGGESTIONS',
            suggestions: result.data?.generateSuggestions.suggestions,
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [state.draft, generateSuggestionsMutation, context, userId],
  );

  const applySuggestion = useCallback(
    async (suggestionId: string) => {
      if (!state.draft) return;

      dispatch({ type: 'APPLY_SUGGESTION_START' });

      try {
        const result = await applySuggestionMutation({
          variables: {
            input: {
              draftId: state.draft.draftId,
              suggestionId,
            },
            userId,
          },
        });

        if (result.data?.applySuggestion) {
          dispatch({
            type: 'APPLY_SUGGESTION_SUCCESS',
            draft: result.data?.applySuggestion.draft,
            definition: result.data?.applySuggestion.currentDefinition,
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [state.draft, applySuggestionMutation, userId],
  );

  const selectSuggestion = useCallback((suggestion: LlmEditSuggestion | null) => {
    dispatch({ type: 'SELECT_SUGGESTION', suggestion });
  }, []);

  // ========================================================================
  // Editing
  // ========================================================================

  const undo = useCallback(() => {
    dispatch({ type: 'LOCAL_UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'LOCAL_REDO' });
  }, []);

  const undoFromServer = useCallback(async () => {
    if (!state.draft) return;

    try {
      const result = await undoLastEditMutation({
        variables: { draftId: state.draft.draftId },
      });

      if (result.data?.undoLastEdit) {
        dispatch({
          type: 'UNDO_SUCCESS',
          draft: result.data?.undoLastEdit.draft,
          definition: result.data?.undoLastEdit.currentDefinition,
        });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.draft, undoLastEditMutation]);

  const updateLocalDefinition = useCallback((definition: WorkflowDefinition) => {
    dispatch({ type: 'LOCAL_UPDATE', definition });
  }, []);

  const setEditMode = useCallback((mode: 'prompt' | 'manual' | 'review') => {
    dispatch({ type: 'SET_EDIT_MODE', mode });
  }, []);

  // ========================================================================
  // Export
  // ========================================================================

  const exportDefinition = useCallback(async () => {
    if (!state.draft) return null;

    try {
      const result = await exportDraftQuery({
        variables: { draftId: state.draft.draftId },
      });

      if (result.data?.exportDraft) {
        return {
          definition: result.data?.exportDraft.definition,
          filename: result.data?.exportDraft.filename,
        };
      }
      return null;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      return null;
    }
  }, [state.draft, exportDraftQuery]);

  // ========================================================================
  // Computed Values
  // ========================================================================

  const canUndo = state.undoStack.length > 0;
  const canRedo = state.redoStack.length > 0;
  const canUndoFromServer = (state.draft?.editHistory.length ?? 0) > 0;
  const hasUnsavedChanges = state.undoStack.length > 0;

  const draftsList = useMemo(() => {
    return draftsData?.listDrafts ?? [];
  }, [draftsData]);

  return {
    state,
    createDraft,
    loadDraft,
    discardDraft,
    persistDraft,
    generateSuggestions,
    applySuggestion,
    selectSuggestion,
    undo,
    redo,
    undoFromServer,
    updateLocalDefinition,
    setEditMode,
    exportDefinition,
    canUndo,
    canRedo,
    canUndoFromServer,
    hasUnsavedChanges,
    draftsList,
    draftsLoading,
    refetchDrafts,
  };
}

export default useLlmWorkflowEditor;
